import { IServerSideGetRowsParams } from '@ag-grid-community/core'
import { GridApi } from '@ag-grid-community/core/dist/cjs/gridApi'
import { countKeys } from '@helios/utils'
import * as _ from 'lodash-es'
import { convertFields } from './convert-fields'
import { convertFilters } from './convert-filter'
import { convertGrouping, GroupModel } from './convert-grouping'
import { convertSortModel, SortModel } from './convert-sort'
import { Filter } from './filters/filter'

/**
 * Request to API for ag-grid server-side model data
 *
 * @see [Ag Grid Server Side Model Documentation](https://www.ag-grid.com/angular-grid/server-side-model/)
 * @template T The tabular data type being requested.
 * @template D Additional data provided in the `context` param.
 */
export interface HeliosServerSideRequest<T, D = null> {
  context?: D
  externalFilters?: Record<string, Filter>
  startRow: number
  endRow: number
  sort: SortModel<T>[]
  filter: { [key in keyof T]?: Filter }
  group: GroupModel<T>
  fields: string[] | undefined
}

export const genericHeliosRequest: HeliosServerSideRequest<unknown> = {
  startRow: 0,
  endRow: 0,
  sort: [],
  filter: {},
  group: {
    keys: [],
    fields: [],
  },
  fields: [],
}

/**
 * Count the total number of applied filter, including ag-grid, quick filter, and advanced filters.
 */
export function countFilters<D extends Record<string, unknown>>(
  gridApi: GridApi,
  context?: D | null,
): number | undefined {
  const appliedFilterModel = gridApi.getFilterModel()
  let count = Object.keys(appliedFilterModel).length

  if (context != null) {
    count += countKeys(context) // adding advanced filters to the count
  }

  return count
}

declare const mandatoryFieldsBrand: unique symbol

export type MandatoryFields = string[] & {
  readonly [mandatoryFieldsBrand]: true
}

export const NO_MANDATORY_FIELDS = [] as unknown as MandatoryFields

/** Convert ag-grid server-side row request to helios row request. */
export function toHeliosRequest<T, D = null>(
  base: IServerSideGetRowsParams,
  excludeFields: string[],
  mandatoryFields: MandatoryFields,
  context?: D,
  // Global setting that will be applied to all numeric filters
  sortByAbsolute?: boolean,
  // Fail safe that ensures that sorting/filtering/grouping is disabled during cache load
  enableCacheDependentFeatures: boolean = true,
): HeliosServerSideRequest<T, D> {
  const {
    request: {
      startRow,
      endRow,
      filterModel,
      groupKeys,
      rowGroupCols,
      sortModel,
    },
    columnApi,
    api,
  } = base

  if (!_.isEqual(api.getFilterModel(), filterModel)) {
    base.fail()
    api.onFilterChanged()
    throw new Error('Filter model in request is out of date!')
  }

  const filter = enableCacheDependentFeatures
    ? convertFilters(filterModel)
    : genericHeliosRequest.filter
  const group = enableCacheDependentFeatures
    ? convertGrouping(rowGroupCols, groupKeys)
    : genericHeliosRequest.group
  const sort = enableCacheDependentFeatures
    ? convertSortModel(sortModel, group, sortByAbsolute)
    : genericHeliosRequest.sort
  const fields = convertFields(columnApi, excludeFields, mandatoryFields)

  return {
    context: enableCacheDependentFeatures ? context : undefined,
    startRow: startRow ?? 0,
    endRow: endRow ?? 0,
    filter,
    group,
    sort,
    fields,
  }
}
